import {useCallback, useEffect, useRef, useState} from 'react';
import {formatDistanceToNow} from 'date-fns';
import {AlertCircle, CheckCircle2, Film, XCircle} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldLabel,
  FieldLegend,
  FieldSet,
  Kbd,
  Spinner,
  Textarea,
  ToggleGroup,
  ToggleGroupItem
} from '@kroll-resolver-crisp/ui-components-react';
import {CopyableUrl} from '@/components/shared/CopyableUrl';
import {type QaReviewItem, type QaReviewOutcome} from '@/api/qaReview';
import {type Classification} from '@/api/classifications';
import {useSubmitQaReview, useClassificationConfig} from '@/api/hooks';
import {getApiErrorMessage} from '@/api/client';
import {ItemMeta} from '@/components/shared/ItemMeta';
import {useTranslatedText} from '@/store/translationStore';
import {ClassificationTree} from '@/components/classify/ClassificationTree';
import {riskVariant} from '@/lib/status';
import {SensitiveMedia} from '@/components/shared/SensitiveMedia';
import {AnalystSubmissionDetails} from '@/components/shared/AnalystSubmissionDetails';
import {ReviewCalibrationNotes} from '@/components/submissions/CalibrationSection';
import {PlatformStatusCell} from '@/components/submissions/SubmissionsTable';
import {SubmissionStatusPill} from '@/components/submissions/SubmissionStatusPill';
import {type Submission} from '@/api/submissions';

type Props = {
  readonly item: QaReviewItem | null;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSubmitted?: () => void;
  /** When reviewing sequentially ("Review All"), shows "N of M" progress in the header. */
  readonly queuePosition?: {current: number; total: number} | undefined;
};

function isTypingTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLTextAreaElement || target instanceof HTMLInputElement;
}

function isOutcome(value: unknown): value is QaReviewOutcome {
  return value === 'true_positive' || value === 'false_positive';
}

export function QaReviewModal({item, onOpenChange, onSubmitted, queuePosition}: Props) {
  const submitReview = useSubmitQaReview();
  const {data: classifications = []} = useClassificationConfig();
  const [outcome, setOutcome] = useState<QaReviewOutcome | null>(null);
  const [notes, setNotes] = useState('');
  const [qaPolicyViolation, setQaPolicyViolation] = useState<Classification | null>(null);
  const [qaRisk, setQaRisk] = useState<Classification | null>(null);
  const [qaNoRisk, setQaNoRisk] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!item) {
      return;
    }

    setOutcome(null);
    setNotes('');
    setQaPolicyViolation(null);
    setQaRisk(null);
    setQaNoRisk(false);
    setError(null);
  }, [item]);

  const translatedTitle = useTranslatedText(item?.id ?? '', item?.title);

  const notesRequired = outcome === 'false_positive';
  const notesMissing = notesRequired && notes.trim() === '';
  // A False Positive means the analyst's original call was wrong, so the SME must record the correct
  // one - scoped to the real classification catalog, not freeform text, so it's directly comparable to
  // original_policy_violation/original_risk - UNLESS the item wasn't a risk at all ("No Risk"), in
  // which case there's nothing to reclassify it as. A True Positive needs no correction either way.
  const correctionRequired = outcome === 'false_positive';
  const correctionMissing = correctionRequired && !qaNoRisk && (!qaPolicyViolation || !qaRisk);
  const canSubmit = outcome !== null && !notesMissing && !correctionMissing;

  const handleSubmit = useCallback(async () => {
    if (!item || !canSubmit || submittingRef.current) {
      return;
    }

    submittingRef.current = true;
    setError(null);
    try {
      await submitReview.mutateAsync({
        id: item.id,
        payload: {
          outcome,
          ...(notes.trim() ? {notes: notes.trim()} : {}),
          ...(outcome === 'false_positive' && qaNoRisk
            ? {qa_no_risk: true}
            : outcome === 'false_positive' && qaPolicyViolation && qaRisk
              ? {qa_policy_violation: qaPolicyViolation.name, qa_risk: qaRisk.name}
              : {})
        }
      });
      onSubmitted?.();
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Failed to submit QA review.'));
    } finally {
      submittingRef.current = false;
    }
  }, [
    item,
    canSubmit,
    outcome,
    notes,
    qaNoRisk,
    qaPolicyViolation,
    qaRisk,
    submitReview,
    onSubmitted
  ]);

  // Keyboard-driven review flow: 1 = True Positive, 2 = False Positive, Enter = Submit, Escape =
  // Cancel (handled for free by the Dialog primitive's default escape-to-close). Digit/Enter
  // shortcuts are suppressed while focus is in the notes textarea so typing "1" or "2" into
  // feedback, or pressing Enter for a newline, doesn't get hijacked.
  useEffect(() => {
    if (!item) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }

      if (event.key === 'Enter') {
        // Buttons (Cancel, the outcome toggles, Submit itself) already do the right thing on
        // Enter via native activation - let that happen instead of hijacking it into a submit.
        if (isTypingTarget(event.target) || event.target instanceof HTMLButtonElement) {
          return;
        }

        event.preventDefault();
        void handleSubmit();

        return;
      }

      if (isTypingTarget(event.target)) {
        return;
      }

      if (event.key === '1') {
        event.preventDefault();
        setOutcome('true_positive');
      } else if (event.key === '2') {
        event.preventDefault();
        setOutcome('false_positive');
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [item, canSubmit, handleSubmit]);

  return (
    <Dialog open={item !== null} onOpenChange={onOpenChange}>
      {/* Always as tall as the dialog may be, rather than as tall as its content: the size stays put
          as Review all steps from item to item, and an odd content height can't centre the dialog on
          a half pixel, which antialiased its edges one of two ways and flaked the screenshot test. */}
      <DialogContent className="h-[calc(100dvh-4rem)] sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Review QA item
            {queuePosition ? (
              <Badge variant="secondary">{`${queuePosition.current} of ${queuePosition.total}`}</Badge>
            ) : null}
          </DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {item ? (
              <>
                <span>{item.analyst_email}</span>
                <span aria-hidden="true">·</span>
                <span>
                  Sampled {formatDistanceToNow(new Date(item.created_at), {addSuffix: true})}
                </span>
                <Badge variant={riskVariant(item.original_risk)}>{item.original_risk}</Badge>
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        {/*
          The body is the one part that scrolls, so the submit button in the sticky footer stays in
          view however long the item's metadata and the classification tree run.
        */}
        <DialogBody>
          {item ? (
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="flex min-w-0 flex-col gap-3">
                <div className="group bg-muted relative aspect-video w-full overflow-hidden">
                  {item.thumbnail_url ? (
                    <SensitiveMedia
                      src={item.thumbnail_url}
                      alt={item.title ?? 'Item preview'}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  ) : (
                    <div className="text-muted-foreground flex h-full flex-col items-center justify-center gap-2">
                      <Film className="size-8" />
                      <span className="text-xs">No preview available</span>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <h3 className="font-heading text-sm leading-snug font-medium">
                    {translatedTitle ?? item.item_external_id ?? item.id}
                  </h3>
                  <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
                    <span>{item.author_name ?? item.author_handle ?? '—'}</span>
                    <Badge variant="outline" className="uppercase">
                      {item.platform}
                    </Badge>
                    {item.published_at ? (
                      <span>
                        {formatDistanceToNow(new Date(item.published_at), {addSuffix: true})}
                      </span>
                    ) : null}
                  </div>
                  {item.url ? <CopyableUrl url={item.url} className="text-xs" /> : null}
                </div>

                {/* What has happened to it since delivery - the context the queue's own columns give. */}
                <div className="flex flex-wrap items-start gap-x-4 gap-y-1 text-xs">
                  <div className="flex flex-col gap-1">
                    <span className="text-muted-foreground">Delivery Status</span>
                    <SubmissionStatusPill
                      status={item.current_delivery_state as Submission['action_status']}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-muted-foreground">Status Check</span>
                    <PlatformStatusCell sub={item.status_check} />
                  </div>
                </div>

                <ItemMeta metadata={item.metadata} itemKey={item.id} title={item.title} />
              </div>

              <div className="flex min-w-0 flex-col gap-4">
                <FieldSet className="gap-0">
                  <FieldLegend variant="label">Analyst classification</FieldLegend>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant="outline">{item.policy_violation}</Badge>
                    <Badge variant={riskVariant(item.original_risk)}>{item.original_risk}</Badge>
                    <Badge variant="secondary">{item.submission.submission_type}</Badge>
                  </div>
                </FieldSet>

                <AnalystSubmissionDetails
                  submission={item.submission}
                  classificationId={item.classification_id}
                />

                <div className="flex flex-col gap-1.5">
                  <span className="text-muted-foreground text-xs">
                    Outcome <Kbd>1</Kbd> / <Kbd>2</Kbd>
                  </span>
                  <ToggleGroup
                    variant="outline"
                    spacing={0}
                    size="sm"
                    aria-label="Outcome"
                    value={outcome ? [outcome] : []}
                    onValueChange={(next) => {
                      const value: unknown = next[0];
                      if (isOutcome(value)) {
                        setOutcome(value);
                      }
                    }}>
                    <ToggleGroupItem value="true_positive">
                      <CheckCircle2 />
                      True positive
                    </ToggleGroupItem>
                    <ToggleGroupItem value="false_positive">
                      <XCircle />
                      False positive
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>

                {outcome === 'false_positive' ? (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium">
                        Correct classification <span className="text-destructive-ink">*</span>
                      </span>
                      <FieldLabel className="text-muted-foreground items-center gap-1.5 text-xs font-normal">
                        <Checkbox
                          checked={qaNoRisk}
                          onCheckedChange={(checked: boolean) => {
                            setQaNoRisk(checked);
                            setQaPolicyViolation(null);
                            setQaRisk(null);
                          }}
                        />
                        Not a risk at all
                      </FieldLabel>
                    </div>

                    {qaNoRisk ? (
                      <Alert>
                        <AlertTitle>Recorded as No Risk</AlertTitle>
                        <AlertDescription>
                          This item didn&apos;t need reclassifying, it shouldn&apos;t have been
                          flagged at all.
                        </AlertDescription>
                      </Alert>
                    ) : qaPolicyViolation && qaRisk ? (
                      <div className="border-border flex items-center justify-between gap-2 rounded-md border p-2">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Badge variant="outline">{qaPolicyViolation.displayName}</Badge>
                          <Badge>{qaRisk.displayName}</Badge>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="xs"
                          onClick={() => {
                            setQaPolicyViolation(null);
                            setQaRisk(null);
                          }}>
                          Change
                        </Button>
                      </div>
                    ) : (
                      <ClassificationTree
                        classifications={classifications}
                        selectedRiskId={qaRisk?.id}
                        onSelect={(policyViolation, risk) => {
                          setQaPolicyViolation(policyViolation);
                          setQaRisk(risk);
                        }}
                      />
                    )}

                    <p className="text-muted-foreground text-xs">
                      Analyst classified as{' '}
                      <span className="text-foreground font-medium">
                        {item.original_policy_violation} / {item.original_risk}
                      </span>
                      .
                    </p>
                    {correctionMissing ? (
                      <p className="text-destructive-ink text-xs">
                        Select the correct policy violation and risk, or mark it as not a risk at
                        all.
                      </p>
                    ) : null}
                  </div>
                ) : null}

                <Field>
                  <FieldLabel htmlFor="qa-review-notes">
                    Notes{' '}
                    {notesRequired ? (
                      <span className="text-destructive-ink">*</span>
                    ) : (
                      <span className="text-muted-foreground font-normal">(optional)</span>
                    )}
                  </FieldLabel>
                  <Textarea
                    id="qa-review-notes"
                    value={notes}
                    placeholder={
                      notesRequired
                        ? 'Explain why this was misclassified…'
                        : 'Optional context for this review…'
                    }
                    onChange={(event) => {
                      setNotes(event.target.value);
                    }}
                  />
                  {notesMissing ? (
                    <FieldDescription className="text-destructive-ink">
                      Notes are required when marking an item as a false positive.
                    </FieldDescription>
                  ) : (
                    <FieldDescription>
                      Feedback is shared with the analyst alongside the outcome.
                    </FieldDescription>
                  )}
                </Field>

                <ReviewCalibrationNotes submissionId={item.submission_id} />

                {error ? (
                  <Alert variant="destructive">
                    <AlertCircle />
                    <AlertTitle>{error}</AlertTitle>
                  </Alert>
                ) : null}
              </div>
            </div>
          ) : null}
        </DialogBody>

        <DialogFooter sticky className="sm:justify-between">
          <span className="text-muted-foreground hidden items-center gap-1 text-xs sm:inline-flex">
            <Kbd>Enter</Kbd> submit · <Kbd>Esc</Kbd> cancel
          </span>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                onOpenChange(false);
              }}>
              Cancel
            </Button>
            <Button
              type="button"
              variant={outcome === 'false_positive' ? 'destructive' : 'default'}
              disabled={!canSubmit || submitReview.isPending}
              onClick={() => void handleSubmit()}>
              {submitReview.isPending ? <Spinner /> : null}
              Submit review
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
